import { Workshop, WorkshopTip } from '@/models/Workshop';
import { WorkshopMeeting } from '@/models/WorkshopMeeting';
import { isBefore } from 'date-fns';
import * as Yup from 'yup';

export interface ProgramClassForm {
    name: string;
    startDate?: Date;
    finishDate?: Date | null | undefined;
    enrollmentsCount?: number;
    enableCourseCertificate?: boolean;
    mandatory?: boolean;
    workshops: Workshop[];
}

export interface WorkshopForm {
    isOnline: boolean;
    address: string;
    host: string;
    meetings: WorkshopMeetingForm[];
    tips?: WorkshopTip[];
}

interface WorkshopMeetingForm {
    startDate: string;
    endDate: string;
}

export const workshopSchema: Yup.ObjectSchema<WorkshopForm> = Yup.object().shape<WorkshopForm>({
    isOnline: Yup.boolean().required(),
    address: Yup.string().when('isOnline', {
        is: true,
        then: (schema: Yup.StringSchema) =>
            schema
                .trim()
                .url()
                .max(255)
                .required(),
        otherwise: (schema: Yup.StringSchema) => schema.trim().required(),
    }),
    host: Yup.string()
        .trim()
        .max(100)
        .required(),
    meetings: Yup.array<WorkshopMeeting>()
        .of(
            Yup.object().shape<WorkshopMeetingForm>({
                startDate: Yup.string().required(),
                endDate: Yup.string()
                    .required()
                    .test('start-end-not-equal', '', function(value) {
                        const startDate: Date = isNaN(this.parent.startDate)
                            ? new Date(this.parent.startDate)
                            : new Date(Number(this.parent.startDate));

                        const endDate: Date = isNaN(value) ? new Date(value) : new Date(Number(value));

                        return isBefore(startDate, endDate);
                    }),
            }),
        )
        .min(1),
    tips: Yup.array<WorkshopTip>()
        .of(
            Yup.object().shape<WorkshopTip>({
                description: Yup.string()
                    .trim()
                    .max(255)
                    .required(),
                workshopTipOrder: Yup.number(),
            }),
        )
        .notRequired(),
});

export const programClassSchema: Yup.ObjectSchema<ProgramClassForm> = Yup.object().shape<ProgramClassForm>({
    name: Yup.string().required(),
    startDate: Yup.date().required(),
    finishDate: Yup.date()
        .notRequired()
        .nullable()
        .default(undefined),
    enrollmentsCount: Yup.number().notRequired(),
    enableCourseCertificate: Yup.bool().notRequired(),
    mandatory: Yup.bool().notRequired(),
    workshops: Yup.array<Workshop>()
        .when('$workshopClass', (workshopClass: boolean) => {
            if (workshopClass) {
                return Yup.array<Workshop>()
                    .required()
                    .test('notEmptyWhenWorkshopClass', '', (workshops: Workshop[]) => {
                        return workshopSchema.isValidSync(workshops[0]);
                    });
            }
            return Yup.array<Workshop>().notRequired();
        })
        .required(),
});

export const workshopFormInitialState = {
    isOnline: true,
    address: '',
    host: '',
    meetings: [] as WorkshopMeeting[],
    tips: [] as WorkshopTip[],
};

export const formInitialState = {
    name: '',
    startDate: undefined,
    finishDate: undefined,
    enrollmentsCount: 0,
    enableCourseCertificate: false,
    mandatory: false,
    workshops: [],
};

export const formErrors = {
    nameError: false,
    startDateError: false,
    finishDateError: false,
    enrollmentsCountError: false,
};
